/* ============================================================================
 * Loading.jsx
 * ============================================================================
 * Componente de indicador de carga reutilizable.
 *
 * Utiliza el componente Ring de @uiball/loaders para mostrar una animación
 * de carga circular. Diseñado para ser usado en cualquier contexto donde
 * se necesite indicar que una operación está en progreso.
 *
 * Uso básico:
 * ```jsx
 * <Loading />
 * ```
 *
 * Uso con tamaño personalizado:
 * ```jsx
 * <Loading size={60} />
 * ```
 *
 * Uso con color personalizado:
 * ```jsx
 * <Loading color="#3498db" />
 * ```
 *
 * @module components/Loading
 * ========================================================================== */

import { Ring } from "@uiball/loaders";

/* ============================================================================
 * Constantes
 * ========================================================================== */

/**
 * Configuración por defecto del indicador de carga.
 * @constant {Object}
 */
const DEFAULT_CONFIG = {
  /** Tamaño del anillo en píxeles */
  SIZE: 40,
  /** Grosor de la línea del anillo */
  LINE_WEIGHT: 5,
  /** Velocidad de la animación (segundos por rotación) */
  SPEED: 2,
  /** Color del anillo — $color-grey-dark (#6b7280), ratio 4.83:1 sobre blanco */
  COLOR: "#6b7280",
};

/* ============================================================================
 * Componente
 * ========================================================================== */

/**
 * Componente de indicador de carga.
 *
 * @param {Object} props - Propiedades del componente
 * @param {number} [props.size=40] - Tamaño del anillo en píxeles
 * @param {number} [props.lineWeight=5] - Grosor de la línea del anillo
 * @param {number} [props.speed=2] - Velocidad de la animación
 * @param {string} [props.color="#6b7280"] - Color del anillo
 * @param {string} [props.className] - Clase CSS adicional para el contenedor
 * @param {string} [props.label] - Texto accesible para lectores de pantalla
 * @returns {JSX.Element} Indicador de carga
 *
 * @example
 * // Uso básico
 * <Loading />
 *
 * @example
 * // Con tamaño grande
 * <Loading size={80} />
 *
 * @example
 * // Con label accesible
 * <Loading label="Cargando archivos..." />
 */
function Loading({
  size = DEFAULT_CONFIG.SIZE,
  lineWeight = DEFAULT_CONFIG.LINE_WEIGHT,
  speed = DEFAULT_CONFIG.SPEED,
  color = DEFAULT_CONFIG.COLOR,
  className = "",
  label = "",
}) {
  return (
    <div
      className={`loading-container ${className}`.trim()}
      role="status"
      aria-label={label || undefined}
    >
      <Ring
        size={size}
        lineWeight={lineWeight}
        speed={speed}
        color={color}
        aria-hidden="true"
      />
      {label && <span className="sr-only">{label}</span>}
    </div>
  );
}

export default Loading;
